import React, { useEffect, useState } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import './AnnotationTooltip.css';

interface AnnotationTooltipProps {
    editor: any;
}

const AnnotationTooltip: React.FC<AnnotationTooltipProps> = ({ editor }) => {
    const [hoveredAnnotation, setHoveredAnnotation] = useState<{
        element: HTMLElement;
        text: string;
    } | null>(null);

    useEffect(() => {
        if (!editor) return;

        const editorDOM = editor.view.dom;
        let hoverTimeout: NodeJS.Timeout | null = null;

        const handleMouseEnter = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const annotationElement = target.closest('.annotation-tooltip') as HTMLElement;
            
            if (annotationElement) {
                const elaborated = annotationElement.getAttribute('data-annotation') || '';
                if (elaborated) {
                    // Small delay to prevent flickering
                    hoverTimeout = setTimeout(() => {
                        setHoveredAnnotation({
                            element: annotationElement,
                            text: elaborated,
                        });
                    }, 150);
                }
            }
        };

        const handleMouseLeave = (e: MouseEvent) => {
            // Clear any pending show timeout
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                hoverTimeout = null;
            }
            
            // Hide tooltip immediately when leaving
            setHoveredAnnotation(null);
        };

        // Use event delegation on the editor DOM
        editorDOM.addEventListener('mouseenter', handleMouseEnter, true);
        editorDOM.addEventListener('mouseleave', handleMouseLeave, true);

        return () => {
            editorDOM.removeEventListener('mouseenter', handleMouseEnter, true);
            editorDOM.removeEventListener('mouseleave', handleMouseLeave, true);
            if (hoverTimeout) {
                clearTimeout(hoverTimeout);
            }
        };
    }, [editor]);

    // Update position on scroll/resize
    useEffect(() => {
        if (!hoveredAnnotation) return;

        const updatePosition = () => {
            setHoveredAnnotation(prev => {
                if (!prev) return null;
                // Force re-render to update position
                return { ...prev };
            });
        };

        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [hoveredAnnotation]);

    if (!hoveredAnnotation) {
        return null;
    }

    const rect = hoveredAnnotation.element.getBoundingClientRect();
    const tooltipStyle: React.CSSProperties = {
        position: 'fixed',
        left: `${rect.left + rect.width / 2}px`,
        top: `${rect.top - 8}px`,
        transform: 'translate(-50%, -100%)',
        zIndex: 10010,
    };

    return (
        <Tooltip.Provider>
            <Tooltip.Root open={true}>
                <Tooltip.Portal>
                    <Tooltip.Content
                        className="annotation-radix-tooltip-content"
                        style={tooltipStyle}
                    >
                        {hoveredAnnotation.text}
                        <Tooltip.Arrow className="annotation-radix-tooltip-arrow" />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
        </Tooltip.Provider>
    );
};

export default AnnotationTooltip;
